import { Injectable } from '@angular/core';
import createKindeClient, { KindeClient, KindeUser } from '@kinde-oss/kinde-auth-pkce-js';
import { environment } from 'environments/environment';

/**
 * KindeAuthService wraps the @kinde-oss/kinde-auth-pkce-js SDK for Angular.
 *
 * Usage:
 * 1. Call `initialize()` once during app init (AppLoadService.initApp)
 * 2. Check `isClientReady()` synchronously before accessing tokens
 * 3. Use `isAuthenticated()`, `getToken()`, `getUser()` for auth state
 * 4. Call `login()` to start the Kinde PKCE flow
 * 5. Call `logout()` to end the session
 *
 * The Kinde SDK handles:
 * - PKCE code challenge/verifier generation
 * - Callback processing (reads `?code=&state=` from the URL on load)
 * - Silent token refresh (getToken() auto-refreshes when near expiry)
 * - Session storage (uses sessionStorage by default, not localStorage)
 */
@Injectable({ providedIn: 'root' })
export class KindeAuthService {
  private client: KindeClient | null = null;
  private _isReady = false;

  /**
   * Initialize the Kinde client. Must be awaited before calling any other method.
   * Safe to call multiple times — subsequent calls are no-ops once ready.
   */
  async initialize(): Promise<void> {
    if (this._isReady) {
      return;
    }

    const redirectUri = environment.kinde?.redirectUri ?? window.location.origin;
    const logoutUri = environment.kinde?.logoutUri ?? window.location.origin;

    this.client = await createKindeClient({
      domain: environment.kinde?.authDomain ?? 'https://auth.statementzen.com',
      client_id: environment.kinde?.clientId ?? '9feee282b10c46a097e805e75632ad24',
      redirect_uri: redirectUri,
      logout_uri: logoutUri,
      audience: environment.kinde?.audience ?? 'https://api.statementzen.com',
    });

    this._isReady = true;
  }

  /**
   * Returns true once `initialize()` has completed.
   * Use this for synchronous guards before calling async methods.
   */
  isClientReady(): boolean {
    return this._isReady;
  }

  /**
   * Returns true if the user has an active Kinde session.
   * This also handles the OAuth callback — when the URL contains `?code=&state=`
   * (Kinde redirect), `createKindeClient` processes it automatically.
   */
  async isAuthenticated(): Promise<boolean> {
    if (!this.client) {
      return false;
    }
    return this.client.isAuthenticated();
  }

  /**
   * Returns the current access token. Kinde SDK auto-refreshes silently
   * when the token is near expiry. Returns undefined if not authenticated.
   */
  async getToken(): Promise<string | undefined> {
    if (!this.client) {
      return undefined;
    }
    return this.client.getToken();
  }

  /**
   * Returns the current user from the Kinde ID token claims.
   * Returns null when not authenticated.
   */
  getUser(): KindeUser | null {
    if (!this.client) {
      return null;
    }
    return this.client.getUser();
  }

  /**
   * Redirects to Kinde login page. After authentication Kinde redirects back
   * to the `redirect_uri` configured in `initialize()`.
   */
  async login(): Promise<void> {
    if (!this.client) {
      throw new Error('KindeAuthService: client not initialized. Call initialize() first.');
    }
    return this.client.login();
  }

  /**
   * Logs the user out and redirects to the `logout_uri`.
   */
  async logout(): Promise<void> {
    if (!this.client) {
      return;
    }
    return this.client.logout();
  }
}
